/**
 * Axis arithmetic for the chart components.
 *
 * The resolvers gap-fill their timeseries, so a chart always receives evenly
 * spaced buckets and its x axis is ordinal — there is no continuous time domain
 * to scale. What is left is the value axis, which needs tick values a reader
 * recognises, and that is all this module does.
 */

/** Steps a reader reads as round. Anything else makes an axis harder, not finer. */
const STEPS = [1, 2, 2.5, 5, 10];

const niceStep = raw => {
  const magnitude = 10 ** Math.floor(Math.log10(raw));
  const normalized = raw / magnitude;

  return (STEPS.find(step => normalized <= step) ?? 10) * magnitude;
};

/**
 * Tick values from zero up to at least `max`.
 *
 * @param {number} max largest value in the series
 * @param {number} [count] rough number of intervals; the result may hold one more
 * @returns {number[]} ascending, always starting at 0
 */
export function ticks(max, count = 4) {
  if (!(max > 0)) return [0, 1];

  const step = niceStep(max / count);
  const highest = Math.ceil(max / step);

  // Rebuilt from the index rather than accumulated, so a fractional step
  // cannot drift a tick onto 0.30000000000000004.
  return Array.from({ length: highest + 1 }, (_, index) =>
    Number((index * step).toPrecision(12))
  );
}

/**
 * Top of the value axis. Bars and lines are measured against this rather than
 * against the peak, so the largest value stops on a gridline instead of just
 * short of one.
 *
 * @param {number} max
 * @param {number} [count]
 * @returns {number}
 */
export function axisMax(max, count = 4) {
  const scale = ticks(max, count);

  return scale[scale.length - 1];
}

/**
 * Where a value sits on the axis, as a percentage of its height.
 *
 * @param {number} value
 * @param {number} max the axis top, not the peak
 * @returns {number} 0–100, clamped
 */
export function share(value, max) {
  if (!(max > 0)) return 0;

  return Math.min(100, Math.max(0, (Number(value) || 0) / max) * 100);
}
